import React, { FC, Fragment, useState, useRef, useEffect } from 'react';
import { useTranslate, ReferenceField } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { time, Longdash } from '~/utils';

import Grid from '@material-ui/core/Grid';
import Accordion from '@material-ui/core/Accordion';
import {
  H2,
  TextField,
  AmountField,
  StatusField,
  AccordionSummary,
  AccordionDetails,
  RunTransferAction,
  LinkToAccount,
  AffiliatedWith,
  ChipField,
  AccountOwner,
  AccountWithErrorWarning,
  TextInput,
  CurrencyAutocomplete,
  SelectInput,
  GlTypeLabel,
} from '~/components';
import { ShowController } from '~/components/ra';
import { Account } from '~/types/account';
import LimitsAndCounters from './LimitsAndCounters';
import NoPermissions from '../NoPermissions';
import AccountCreate from './AccountCreate';
import { useAccountPermissions, useGetAllAccountsWithHeaders } from '~/hooks/accounts';
import EditSourceName from '~/components/EditSourceName';
import BalancesTable from './BalancesTable';
import Alert from '@material-ui/lab/Alert';
import { withStyles } from '@material-ui/core/styles';
import EditActions from '~/components/EditActions';
import BlockAccountButton from '~/components/AccountActionButtons/BlockAccountButton';
import UnunblockAccountButton from '~/components/AccountActionButtons/UnblockAccountButton';
import CloseAccountButton from '~/components/AccountActionButtons/CloseAccountButton';
import TablePagination from '@material-ui/core/TablePagination';
import { useHandbooksQuery } from '~/hooks/useHandbook';
import AccountTableSingleSelect from '~/components/AccountTableSingleSelect/AccountTableSingleSelect';
import debounce from 'lodash/debounce';
import { ACCOUNTS_PRODUCT_CATEGORIES } from '~/utils/constants/accounts';
import { rowsPerPageOptionsDefault, perPageDefault } from '~/utils/pagination';

type FilterType = 'status' | 'number' | 'alias' | 'currency';

// Style AccordionSummary to prevent focused grey color when clicking inside of dialog.
const AccordionSummaryStyled = withStyles({
  root: {
    '&.Mui-focused': {
      backgroundColor: 'inherit',
    },
  },
})((props: any) => {
  return <AccordionSummary {...props} />;
});

export const l = (key: string): string => `components.ra.AccountList.${key}`;

const getTotalCountFromContentRange = (headers: any): number => {
  const contentRange = headers?.['content-range'];
  if (!contentRange) return 0;
  const total = contentRange.split('/')[1];
  return parseInt(total, 10);
};

interface Props {
  resource: 'businesses' | 'individuals' | 'partners';
  profileId: string;
  basePath: string;
  permissions: string[];
  solutionId?: string;
}

const Accounts = (props: Props) => {
  const { resource, profileId, basePath, permissions, solutionId } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const classes = useStyles();

  const {
    hasBlockPermission,
    hasUnblockPermission,
    hasClosePermission,
    hasAliasUpdatePermission,
    hasListPermission,
    hasCreatePermission,
  } = useAccountPermissions('accounts', resource);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(perPageDefault);
  const [allFilters, setAllFilters] = useState<{ [key in FilterType]?: any }>({});
  const [debouncedFilters, setDebouncedFilters] = useState<{ [key in FilterType]?: any }>({});
  const [totalCount, setTotalCount] = useState(0);

  // Debounce only the filter effect, not the input value
  const debouncedFilterEffect = useRef(
    debounce((filters) => {
      setDebouncedFilters(filters);
    }, 500)
  ).current;

  useEffect(() => {
    debouncedFilterEffect(allFilters);
  }, [allFilters, debouncedFilterEffect]);

  const { data, isLoading: loading, refetch } = useGetAllAccountsWithHeaders(
    {
      resource,
      profileId,
      pagination: { page, perPage: rowsPerPage },
      filters: { ...debouncedFilters, excludedProductCategories: ACCOUNTS_PRODUCT_CATEGORIES },
    },
    {
      enabled: !!profileId,
      onSuccess: (data) => {
        setTotalCount(getTotalCountFromContentRange(data?.headers));
      },
    }
  );
  const accounts = data?.data?.filter(
    (item: Account | undefined) => item?.productTemplate?.category !== 'CNL_ACCOUNT'
  );

  // ExpansionPanel
  const [expanded, setExpanded] = useState<{ [x: string]: boolean }>({});
  const handleChange = (key: string, isExpanded: boolean) => () => {
    setExpanded((prevProps) => ({ ...prevProps, [key]: !isExpanded }));
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    const { name, value } = event.target;
    setAllFilters((prev: any) => ({ ...prev, [name]: value ? value : undefined }));
  };

  const { data: handbook } = useHandbooksQuery(solutionId);
  const currencies = handbook?.currencies;

  if (!permissions || hasListPermission === undefined) return null;
  if (!hasListPermission) return <NoPermissions />;

  const hasAccountViewPermission = permissions.includes('account.view');

  const hasRunTransferPermission =
    permissions.includes('settlement.runTransfer') ||
    permissions.includes('accountTransfer.runTransfer') ||
    permissions.includes('debitOrder.runTransfer') ||
    permissions.includes('serviceCharge.runTransfer');

  const findFilter = (key: FilterType) => {
    switch (key) {
      case 'status':
        return (
          <SelectInput
            label={t('status._')}
            options={[
              { value: 'ACTIVE', text: t('status.ACTIVE') },
              { value: 'BLOCKED', text: t('status.BLOCKED') },
              { value: 'CLOSED', text: t('status.CLOSED') },
            ]}
            allowEmpty
            emptyText={Longdash}
            onChange={handleFilterChange}
            value={allFilters.status}
            name='status'
            disableHelperText
          />
        );
      case 'number':
        return (
          <AccountTableSingleSelect
            resource={resource}
            profileId={profileId}
            label={t('accountNumber')}
            value={allFilters.number || ''}
            onChange={(account) => {
              setPage(1);
              setAllFilters((prev) => ({ ...prev, number: account.number }));
            }}
            onClear={() => {
              setPage(1);
              setAllFilters((prev) => ({ ...prev, number: undefined }));
            }}
          />
        );
      case 'alias':
        return (
          <TextInput
            label={t('alias')}
            value={allFilters.alias}
            onChange={handleFilterChange}
            name='alias'
            disableHelperText
          />
        );
      case 'currency':
        return (
          <CurrencyAutocomplete
            label={t('currency')}
            onChange={handleFilterChange}
            value={allFilters.currency}
            name='currency'
            filterOptions={(all) => all.filter((c) => currencies?.find((hc) => hc.id === c))}
            disableHelperText
          />
        );
      default:
        return null;
    }
  };

  const renderFilters = (filters: FilterType[]) => {
    return (
      <Grid container spacing={1}>
        {filters.map((filter, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            {findFilter(filter)}
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <H2 topSpacing={0} bottomSpacing={0}>
          {t(`accounts`)}
        </H2>
        <div id='section-actions'>
          {hasCreatePermission && (
            <AccountCreate
              refetch={refetch}
              resource={resource}
              solutionId={solutionId as string}
              profileId={profileId}
            />
          )}
          {hasRunTransferPermission && (
            <RunTransferAction
              profileId={profileId}
              resource={resource}
              onDone={() => {
                refetch();
              }}
              permissions={permissions}
            />
          )}
        </div>
      </header>
      <div className={classes.filters}>
        {renderFilters(['status', 'number', 'alias', 'currency'])}
      </div>
      {loading && <div>{t('searchInProgress')}</div>}
      {!loading && !accounts?.length && <div>{t('noAccounts')}</div>}
      {!loading &&
        accounts?.map((account: Account, index: number) => {
          const {
            alias,
            balances,
            number,
            name,
            status,
            created,
            error,
            glType,
            id: accountId,
          } = account as Account;
          const key = (account as Account).id as string;
          const isExpanded = Boolean(expanded[key]);
          const currentAvailable = balances?.find(
            (item) => item.code === 'CURRENT_AVAILABLE' || item.code === 'CURRENT_FLOAT'
          )?.amount;
          const heading = (
            <Grid container alignItems='center'>
              {alias ? (
                `${alias} (${number})`
              ) : (
                <ReferenceField
                  source='productId'
                  reference='products'
                  link={false}
                  addLabel={false}
                  record={account}
                  basePath={basePath}
                >
                  <ShowController>
                    {({ showRecord }) => {
                      return <div>{`${showRecord?.name || 'Unnamed account'} (${number})`}</div>;
                    }}
                  </ShowController>
                </ReferenceField>
              )}
              {account.profileId !== profileId && (
                <ChipField style={{ margin: '0 10px' }} label={t('shared')} />
              )}
            </Grid>
          );
          const menuItems = [
            hasAccountViewPermission && <LinkToAccount id={account.id} />,
            hasAliasUpdatePermission && (
              <EditSourceName
                source='accounts'
                resource={resource}
                record={account}
                refetch={refetch as any}
              />
            ),
            hasBlockPermission && status === 'ACTIVE' && (
              <BlockAccountButton record={account} refetch={refetch} resource='accounts' />
            ),
            hasUnblockPermission && status === 'BLOCKED' && (
              <UnunblockAccountButton record={account} refetch={refetch} resource='accounts' />
            ),
            hasClosePermission && status !== 'CLOSED' && (
              <CloseAccountButton record={account} refetch={refetch} resource='accounts' />
            ),
          ].filter(Boolean);
          return (
            <Grid key={index} item xs={12} className={classes.account}>
              <Accordion expanded={isExpanded} onChange={handleChange(key, isExpanded)}>
                <AccordionSummaryStyled
                  aria-controls={`${key}-content`}
                  id={`${key}-header`}
                  expanded={isExpanded}
                  heading={heading}
                  actionNode={
                    <Grid container justify='flex-end' spacing={1}>
                      <Grid item alignItems='center' className={classes.balanceContainer}>
                        {currentAvailable && <AmountField amount={currentAvailable} />}
                      </Grid>
                      {menuItems.length > 0 && <EditActions menuItems={menuItems} />}
                    </Grid>
                  }
                  actionAlwaysVisible
                  content={
                    <Grid container spacing={3}>
                      {error && (
                        <Grid item xs={12}>
                          <AccountWithErrorWarning
                            accountId={accountId}
                            error={error}
                            refetch={refetch}
                          />
                        </Grid>
                      )}
                      <Grid item xs={3}>
                        <TextField label={t('number')} loading={loading} copyable={number} ellipsis>
                          <GlTypeLabel glType={glType} text={number} />
                        </TextField>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField label={t('name')} loading={loading} copyable={name} ellipsis>
                          {name ? name : Longdash}
                        </TextField>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField label={t('created')} loading={loading}>
                          {time(created).format('L')}
                        </TextField>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField label={t('status._')} loading={loading}>
                          <StatusField status={status} />
                        </TextField>
                      </Grid>
                    </Grid>
                  }
                />
                <AccordionDetails>
                  {isExpanded && (
                    <AccountData
                      data={account}
                      loading={loading}
                      resource={resource}
                      profileId={profileId}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
      {!loading && totalCount > perPageDefault && (
        <TablePagination
          className={classes.pagination}
          rowsPerPageOptions={rowsPerPageOptionsDefault}
          component='div'
          count={totalCount}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
    </div>
  );
};

interface AccountData {
  data: Account | object;
  loading: boolean;
  resource: string;
  profileId: string;
}

const AccountData: FC<AccountData> = (props) => {
  const { data, loading, resource, profileId } = props;
  const {
    name,
    balances,
    id: accountId,
    references,
    profileId: accountProfileId,
    profileType: accountProfileType,
    closed,
    productTemplate,
  } = data as Account;
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const isFallback = productTemplate?.code === 'FALLBACK';

  return (
    <Fragment>
      <Grid container spacing={2} className={classes.container}>
        {name && (
          <Grid item xs={12}>
            <TextField label={t('name')}>{name}</TextField>
          </Grid>
        )}
        {closed && (
          <Grid item xs={4}>
            <TextField label={t('closedOn')} copyable>
              {time(closed).format('L')}
            </TextField>
          </Grid>
        )}
        {isFallback ? (
          <Alert severity='warning'>{t('productTemplateFallback')}</Alert>
        ) : (
          <>
            <Grid item xs={12}>
              <BalancesTable balances={balances} loading={loading} resource={resource} />
            </Grid>
            {accountId && (
              <Grid item xs={12}>
                <LimitsAndCounters id={accountId} />
              </Grid>
            )}
          </>
        )}

        {data && accountProfileId !== profileId && (
          <Grid item xs={12}>
            <AccountOwner
              profileId={accountProfileId}
              profileType={accountProfileType}
              noPaddingLeft
              topSpacing={2}
              withIcon
            />{' '}
          </Grid>
        )}
        <Grid item xs={12}>
          <AffiliatedWith references={references} type='SHARED_WITH' withIcon noPaddingLeft />
        </Grid>
      </Grid>
    </Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(2),
  },
  container: {
    paddingLeft: theme.spacing(2),
  },
  account: {
    '& .account-actions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover .account-actions': {
      opacity: 1,
    },
  },
  section: {
    '& header #section-actions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& header #section-actions': {
        opacity: 1,
      },
    },
  },
  header: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  balanceContainer: {
    display: 'flex',
    fontSize: '16px',
  },
  filters: {
    display: 'flex',
    marginBottom: theme.spacing(2),
  },
  pagination: {
    '& .MuiTablePagination-toolbar': {
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
}));

export default Accounts;
